import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from 'store';
import { ValidationMessage } from 'types/validation';
import { validationResultUtils } from 'utils/validation';
import { setValidationResults } from '../store/wizardSlice';

export function useWizardValidationResults() {
    const dispatch = useAppDispatch();
    const { validationResults } = useSelector((state: RootState) => state.wizard);

    const globalErrors = useMemo(() => {
        const errors: ValidationMessage[] = [];
        for (const result in validationResults) {
            const validationResult = validationResults[result];
            if (!validationResult) continue;
            const msgs = validationResultUtils.getAllGlobalErrors(validationResult);
            errors.push(...msgs);
        }
        return errors.map((m) => m.message);
    }, [validationResults]);

    const globalWarnings = useMemo(() => {
        const warnings: ValidationMessage[] = [];
        for (const result in validationResults) {
            const validationResult = validationResults[result];
            if (!validationResult) continue;
            const msgs = validationResultUtils.getAllGlobalWarnings(validationResult);
            warnings.push(...msgs);
        }
        return warnings.map((m) => m.message);
    }, [validationResults]);

    const clearGlobalErrors = () => {
        const vldResults = structuredClone(validationResults);
        for (const key in vldResults) {
            const vldResult = vldResults[key];
            if (!vldResult) continue;

            if (vldResult.globalIssues) {
                vldResult.globalIssues = vldResult.globalIssues.filter((issue) => issue.severity !== 'Error');
            }

            if (vldResult.issues) {
                for (const issue of vldResult.issues) {
                    if (issue.fields.length > 0) {
                        issue.fields = issue.fields.map((field) => {
                            return {
                                ...field,
                                validationMessages: field.validationMessages.filter((msg) => msg.severity !== 'Error'),
                            };
                        });
                    }
                    if (issue?.generalValidationMessages) {
                        issue.generalValidationMessages = issue.generalValidationMessages.filter(
                            (msg) => msg.severity !== 'Error'
                        );
                    }
                }
            }
        }
        dispatch(setValidationResults(vldResults));
    };

    const clearGlobalWarnings = () => {
        const vldResults = structuredClone(validationResults);

        for (const key in vldResults) {
            const vldResult = vldResults[key];
            if (!vldResult) continue;

            if (vldResult.globalIssues) {
                vldResult.globalIssues = vldResult.globalIssues.filter((issue) => issue.severity !== 'Warning');
            }

            if (vldResult.issues) {
                for (const issue of vldResult.issues) {
                    if (issue.fields.length > 0) {
                        issue.fields = issue.fields.map((field) => {
                            return {
                                ...field,
                                validationMessages: field.validationMessages.filter(
                                    (msg) => msg.severity !== 'Warning'
                                ),
                            };
                        });
                    }
                    if (issue?.generalValidationMessages) {
                        issue.generalValidationMessages = issue.generalValidationMessages.filter(
                            (msg) => msg.severity !== 'Warning'
                        );
                    }
                }
            }
        }
        dispatch(setValidationResults(vldResults));
    };

    return {
        globalErrors,
        globalWarnings,
        clearGlobalErrors,
        clearGlobalWarnings,
    };
}
